import { buildBracket } from './bracketUtils';

/**
 * Placeholder "loser of match N" slot entries feeding a consolation bracket —
 * there's no real result yet (mock/pre-play), so each direction bracket is
 * seeded with a labeled placeholder per losing slot rather than a participant.
 * Cites the SOURCE bracket's own match number (e.g. "Loser of West Match 3"),
 * which is unambiguous even across rounds — unlike a round-label
 * ("Semifinal") which means a different thing depending on bracket size.
 */
const losersOf = (directionName, matches) =>
  matches.map((match) => ({
    id: `loser-${directionName}-m${match.number}`.replace(/\s+/g, ''),
    name: `Loser of ${directionName} Match ${match.number}`,
    isPlaceholder: true,
  }));

/**
 * Compass Draw: nobody is fully eliminated after one loss. The main (East)
 * bracket runs single elimination; every round's losers drop into a
 * direction-named consolation bracket instead of going home. Which of the
 * 8 directions actually populate is entirely a function of how many rounds
 * East has — smaller draws only ever reach W/N, larger draws (16, 32+)
 * populate all 8. No manual "number of rounds" input exists or is needed.
 */
export const generateCompassDraw = (participants) => {
  const n = participants.length;
  if (n < 4) {
    throw new Error('Compass Draw needs at least 4 participants');
  }

  // Built in dependency order, not the compass's spatial layout order (NW N
  // NE / W E / SW S SE) — a direction bracket's own Round 1 can't be played
  // until whichever bracket feeds its losers has reached the round it draws
  // from, so `activeDirections` (used by the List view to lay out every
  // direction's schedule as one continuous, playable sequence) has to follow
  // that same dependency chain: E before W before N before NE, W before NW,
  // E before S before SW before SE. The Bracket view's grid (CompassGrid.jsx)
  // doesn't read this array's order at all — it has its own fixed spatial
  // STACK_ORDER and only checks membership here — so reordering this list is
  // safe with respect to that view.
  const groups = {};
  const activeDirections = [];
  const addGroup = (direction, group) => {
    groups[direction] = group;
    activeDirections.push(direction);
  };

  const east = buildBracket(participants, 'E');
  addGroup('E', east);

  const eR1Matches = east.rounds[0].matches;
  if (eR1Matches.length >= 2) {
    addGroup('W', buildBracket(losersOf('Main Draw', eR1Matches), 'W'));

    const wR1Matches = groups.W.rounds[0].matches;
    if (wR1Matches.length >= 2) {
      addGroup('N', buildBracket(losersOf('West', wR1Matches), 'N'));

      const nR1Matches = groups.N.rounds[0].matches;
      if (nR1Matches.length >= 2) {
        addGroup('NE', buildBracket(losersOf('North', nR1Matches), 'NE'));
      }
    }

    if (groups.W.rounds.length >= 2) {
      const wR2Matches = groups.W.rounds[1].matches;
      if (wR2Matches.length >= 2) {
        addGroup('NW', buildBracket(losersOf('West', wR2Matches), 'NW'));
      }
    }
  }

  if (east.rounds.length >= 2) {
    const eR2Matches = east.rounds[1].matches;
    if (eR2Matches.length >= 2) {
      addGroup('S', buildBracket(losersOf('Main Draw', eR2Matches), 'S'));

      const sR1Matches = groups.S.rounds[0].matches;
      if (sR1Matches.length >= 2) {
        addGroup('SW', buildBracket(losersOf('South', sR1Matches), 'SW'));

        // Southeast mirrors Northeast: one level deeper down the Southwest chain,
        // same as Northeast is one level deeper down the North chain. Needs a
        // large draw (32+) before Southwest itself has enough round-1 losers.
        const swR1Matches = groups.SW.rounds[0].matches;
        if (swR1Matches.length >= 2) {
          addGroup('SE', buildBracket(losersOf('Southwest', swR1Matches), 'SE'));
        }
      }
    }
  }

  return {
    formatId: 'compass_draw',
    participantsCount: n,
    activeDirections,
    groups,
  };
};
